import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import importsService from '../../services/imports'
import './ImportNotificationBadge.scss'

const POLL_INTERVAL = 5000 // 5 seconds

const ImportNotificationBadge = () => {
  const [importStatus, setImportStatus] = useState(null) // null, 'processing', 'completed', 'failed'
  const [importData, setImportData] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const trackedImportIdRef = useRef(null)
  const pollRef = useRef(null)
  const dropdownRef = useRef(null)
  const navigate = useNavigate()

  // Check localStorage for in-progress import and poll for updates
  useEffect(() => {
    const checkImport = async () => {
      const importId = localStorage.getItem('inProgressImportId')

      // If no import in progress, clear state
      if (!importId) {
        trackedImportIdRef.current = null
        setImportStatus(null)
        setImportData(null)
        return
      }

      // If import ID changed, reset state and start tracking new one
      if (importId !== trackedImportIdRef.current) {
        trackedImportIdRef.current = importId
        setImportStatus('processing')
        setImportData(null)
      }

      try {
        const data = await importsService.getImport(importId)
        setImportData(data)

        if (data.status === 'completed' || data.status === 'failed') {
          setImportStatus(data.status)
        } else {
          setImportStatus('processing')
        }
      } catch (err) {
        console.error('Failed to check import status:', err)
        // If we can't fetch, clear it
        localStorage.removeItem('inProgressImportId')
        trackedImportIdRef.current = null
        setImportStatus(null)
        setImportData(null)
      }
    }

    // Initial check
    checkImport()

    // Keep polling to detect new imports and status changes
    pollRef.current = setInterval(checkImport, POLL_INTERVAL)

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleDismiss = () => {
    localStorage.removeItem('inProgressImportId')
    trackedImportIdRef.current = null
    setImportStatus(null)
    setImportData(null)
    setIsOpen(false)
  }

  const handleViewResults = () => {
    if (importData?.importId) {
      navigate(`/admin/imports/${importData.importId}`)
    }
    setIsOpen(false)
  }

  // Don't show if no import
  if (!importStatus) {
    return null
  }

  const getStatusIcon = () => {
    switch (importStatus) {
      case 'processing':
        return '⏳'
      case 'completed':
        return '✅'
      case 'failed':
        return '❌'
      default:
        return '📥'
    }
  }

  const getStatusText = () => {
    switch (importStatus) {
      case 'processing':
        return 'Import in progress...'
      case 'completed':
        return 'Import completed!'
      case 'failed':
        return 'Import failed'
      default:
        return 'Import'
    }
  }

  const showBadge = importStatus === 'completed' || importStatus === 'failed'

  return (
    <div className="import-notification-badge" ref={dropdownRef}>
      <button
        className={`import-notification-badge__button import-notification-badge__button--${importStatus}`}
        onClick={() => setIsOpen(!isOpen)}
        title={getStatusText()}
      >
        <span className="import-notification-badge__icon">{getStatusIcon()}</span>
        {showBadge && (
          <span className="import-notification-badge__dot" />
        )}
      </button>

      {isOpen && (
        <div className="import-notification-badge__dropdown">
          <div className="import-notification-badge__header">
            <span className="import-notification-badge__header-icon">{getStatusIcon()}</span>
            <span className="import-notification-badge__header-text">{getStatusText()}</span>
          </div>

          {importData && (
            <div className="import-notification-badge__details">
              <div className="import-notification-badge__detail-row">
                <span className="import-notification-badge__detail-label">Type:</span>
                <span className="import-notification-badge__detail-value">{importData.type}</span>
              </div>
              <div className="import-notification-badge__detail-row">
                <span className="import-notification-badge__detail-label">File:</span>
                <span className="import-notification-badge__detail-value">{importData.fileName}</span>
              </div>
              {importStatus !== 'processing' && (
                <>
                  <div className="import-notification-badge__detail-row">
                    <span className="import-notification-badge__detail-label">Successful:</span>
                    <span className="import-notification-badge__detail-value import-notification-badge__detail-value--success">
                      {importData.results?.successful ?? 0}
                    </span>
                  </div>
                  <div className="import-notification-badge__detail-row">
                    <span className="import-notification-badge__detail-label">Failed:</span>
                    <span className="import-notification-badge__detail-value import-notification-badge__detail-value--error">
                      {importData.results?.failed ?? 0}
                    </span>
                  </div>
                </>
              )}
              {importStatus === 'processing' && importData.progress?.processed !== undefined && (
                <div className="import-notification-badge__detail-row">
                  <span className="import-notification-badge__detail-label">Progress:</span>
                  <span className="import-notification-badge__detail-value">
                    {importData.progress.processed} / {importData.progress.total}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="import-notification-badge__actions">
            {(importStatus === 'completed' || importStatus === 'failed') && (
              <button
                className="btn btn-sm btn-primary"
                onClick={handleViewResults}
              >
                View Results
              </button>
            )}
            <button
              className="btn btn-sm btn-secondary"
              onClick={handleDismiss}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ImportNotificationBadge
